import React, { useMemo, useState, useRef, useCallback, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  MapContainer, TileLayer, Marker, Popup, ZoomControl, useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import MapTileToggle from "@/components/maps/MapTileToggle";
import {
  ArrowRight, Mountain, Tent, Building, Compass, Search, X, Plus, Check,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useBooking } from "@/contexts/BookingContext";
import { HOTELS } from "@/lib/data";
import { HOTEL_COORDS, LANDMARKS, MAP_CENTER, MAP_BOUNDS } from "@/lib/mapData";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

// Branded SVG marker, with optional active state.
const buildMarker = (color, glyph, active = false) =>
  L.divIcon({
    className: "xaluca-marker",
    html: `
      <div style="position:relative;display:flex;align-items:center;justify-content:center;">
        <span style="
          width:${active ? 38 : 30}px;height:${active ? 38 : 30}px;border-radius:50%;
          background:${color};
          box-shadow:${active
            ? "0 6px 16px rgba(193,101,66,0.55),0 0 0 5px rgba(212,163,115,0.55),0 0 0 8px rgba(253,251,247,0.95)"
            : "0 4px 10px rgba(28,22,18,0.35),0 0 0 4px rgba(253,251,247,0.85)"};
          display:flex;align-items:center;justify-content:center;
          color:#FDFBF7;font-weight:600;font-size:${active ? 16 : 14}px;font-family:'Outfit',sans-serif;
          transform:translateY(-50%);
          transition: all 0.18s ease;
        ">${glyph}</span>
      </div>
    `,
    iconSize: [active ? 38 : 30, active ? 38 : 30],
    iconAnchor: [active ? 19 : 15, active ? 38 : 30],
    popupAnchor: [0, -28],
  });

const COLORS = {
  hotel: "#C16542",
  bivouac: "#2C2621",
  refuge: "#5C7C4F",
  landmark: "#D4A373",
};
const GLYPHS = {
  hotel: "▲",
  bivouac: "▲",
  refuge: "▲",
  landmark: "●",
};

const CATEGORY_LABEL = {
  hotel: { en: "Hotel", fr: "Hôtel", es: "Hotel" },
  bivouac: { en: "Bivouac", fr: "Bivouac", es: "Bivac" },
  refuge: { en: "Refuge", fr: "Refuge", es: "Refugio" },
  landmark: { en: "Landmark", fr: "Site emblématique", es: "Hito" },
};

const FILTERS = [
  { id: "hotel", icon: Building, label: { en: "Hotels", fr: "Hôtels", es: "Hoteles" } },
  { id: "bivouac", icon: Tent, label: { en: "Bivouacs", fr: "Bivouacs", es: "Bivacs" } },
  { id: "refuge", icon: Mountain, label: { en: "Refuges", fr: "Refuges", es: "Refugios" } },
  { id: "landmark", icon: Compass, label: { en: "Landmarks", fr: "Sites", es: "Hitos" } },
];

// Captures the map instance once mounted.
const MapHandle = ({ onReady }) => {
  const map = useMap();
  useEffect(() => {
    onReady(map);
  }, [map, onReady]);
  return null;
};

const MapSection = ({ initialFilters } = {}) => {
  const { lang } = useLanguage();
  const { selectedHotels, toggleHotelWithFeedback } = useBooking();
  const navigate = useNavigate();
  const [active, setActive] = useState({
    hotel: initialFilters?.hotel ?? true,
    bivouac: initialFilters?.bivouac ?? true,
    refuge: initialFilters?.refuge ?? true,
    landmark: initialFilters?.landmark ?? true,
  });
  const [query, setQuery] = useState("");
  const [focusedId, setFocusedId] = useState(null);
  const mapRef = useRef(null);
  const markerRefs = useRef({});
  const panelItemRefs = useRef({});

  // Build the unified marker list (memoised by lang).
  const allPlaces = useMemo(() => {
    const hotels = HOTELS.map((h) => {
      const coords = HOTEL_COORDS[h.slug];
      if (!coords) return null;
      const cat = h.category || "hotel";
      return {
        key: `h-${h.slug}`,
        slug: h.slug,
        kind: cat,
        lat: coords.lat,
        lng: coords.lng,
        name: h.name,
        region: pick({ en: h.region, fr: h.region_fr || h.region, es: h.region_es || h.region }, lang),
        location: h.location,
        intro: pick(h.intro, lang),
        href: `/hotels/${h.slug}`,
      };
    }).filter(Boolean);
    const lms = LANDMARKS.map((l) => ({
      key: `l-${l.id}`,
      kind: "landmark",
      lat: l.lat,
      lng: l.lng,
      name: pick(l.name, lang),
      region: pick(l.type, lang),
      intro: pick(l.desc, lang),
      href: null,
    }));
    return [...hotels, ...lms];
  }, [lang]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allPlaces.filter((m) => {
      if (!active[m.kind]) return false;
      if (!q) return true;
      return (
        m.name.toLowerCase().includes(q) ||
        (m.region || "").toLowerCase().includes(q) ||
        (m.intro || "").toLowerCase().includes(q)
      );
    });
  }, [allPlaces, active, query]);

  // Group panel list by category for legibility.
  const grouped = useMemo(() => {
    const g = { hotel: [], bivouac: [], refuge: [], landmark: [] };
    visible.forEach((p) => g[p.kind]?.push(p));
    return g;
  }, [visible]);

  const handleReady = useCallback((m) => {
    mapRef.current = m;
  }, []);

  const focusPlace = useCallback((place) => {
    setFocusedId(place.key);
    const map = mapRef.current;
    if (map) {
      // Zoom in enough to spread overlapping pins (Merzouga / Erg Chebbi cluster).
      map.flyTo([place.lat, place.lng], 11, { duration: 0.9 });
      // Open popup after flyTo settles.
      window.clearTimeout(focusPlace._t);
      focusPlace._t = window.setTimeout(() => {
        const mk = markerRefs.current[place.key];
        if (mk && mk.openPopup) mk.openPopup();
      }, 950);
    }
  }, []);

  const toggle = (id) => setActive((p) => ({ ...p, [id]: !p[id] }));

  return (
    <section
      id="map"
      className="py-20 md:py-28 bg-[#FBF6EE]/70 relative overflow-hidden"
      data-testid="section-map"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="grid md:grid-cols-12 gap-10 items-end mb-10">
          <div className="md:col-span-7">
            <p className="overline">
              {pick({ en: "Where we are", fr: "Où nous sommes", es: "Dónde estamos" }, lang)}
            </p>
            <h2 className="font-serif-x mt-4 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#2C2621]">
              {pick({
                en: "The map of southern Morocco.",
                fr: "La carte du sud du Maroc.",
                es: "El mapa del sur de Marruecos.",
              }, lang)}
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="text-base text-[#5C5248] leading-relaxed">
              {pick({
                en: "From the cedar lakes of the Middle Atlas to the dunes of Erg Chebbi — every Xaluca property, every landmark, and a side index for places that sit close together.",
                fr: "Des lacs des cèdres du Moyen Atlas aux dunes de l'Erg Chebbi — chaque adresse Xaluca, chaque site, et un index latéral pour les lieux qui se chevauchent.",
                es: "De los lagos de cedros del Atlas Medio a las dunas de Erg Chebbi — todas las direcciones Xaluca, todos los hitos y un índice lateral para los lugares que se superponen.",
              }, lang)}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-5" data-testid="map-filters">
          {FILTERS.map((f) => {
            const Icon = f.icon;
            const on = active[f.id];
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => toggle(f.id)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-[11px] tracking-[0.2em] uppercase border transition-colors ${
                  on
                    ? "bg-[#2C2621] text-[#FDFBF7] border-[#2C2621]"
                    : "bg-white text-[#5C5248] border-[#E8DFD2] hover:border-[#C16542]"
                }`}
                data-testid={`map-filter-${f.id}`}
                aria-pressed={on}
              >
                <span
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ background: COLORS[f.id], opacity: on ? 1 : 0.5 }}
                />
                <Icon className="w-3.5 h-3.5" strokeWidth={1.6} />
                {pick(f.label, lang)}
              </button>
            );
          })}
          <span className="ml-auto text-[11px] tracking-[0.25em] uppercase text-[#5C5248]" data-testid="map-count">
            {visible.length} {pick({ en: "places", fr: "lieux", es: "lugares" }, lang)}
          </span>
        </div>

        {/* Map + Side panel */}
        <div className="grid lg:grid-cols-12 gap-4 lg:gap-5 items-stretch">
          {/* Map */}
          <div
            className="lg:col-span-8 relative rounded-3xl overflow-hidden border border-[#E8DFD2] shadow-[0_8px_30px_-12px_rgba(44,38,33,0.35)]"
            style={{ height: "640px" }}
          >
            <MapContainer
              center={MAP_CENTER}
              zoom={6}
              bounds={MAP_BOUNDS}
              scrollWheelZoom={false}
              zoomControl={false}
              style={{ height: "100%", width: "100%" }}
              data-testid="map-container"
            >
              <MapHandle onReady={handleReady} />
              <MapTileToggle
                mapUrl="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                mapAttribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
                mapSubdomains={['a','b','c','d']}
                mapMaxZoom={20}
              />
              <ZoomControl position="bottomright" />
              {visible.map((m) => (
                <Marker
                  key={m.key}
                  position={[m.lat, m.lng]}
                  icon={buildMarker(COLORS[m.kind], GLYPHS[m.kind], focusedId === m.key)}
                  ref={(ref) => {
                    if (ref) markerRefs.current[m.key] = ref;
                  }}
                  eventHandlers={{
                    click: () => {
                      setFocusedId(m.key);
                      const el = panelItemRefs.current[m.key];
                      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                    },
                    popupclose: () => setFocusedId((cur) => (cur === m.key ? null : cur)),
                  }}
                >
                  <Popup>
                    <div style={{ minWidth: "220px", maxWidth: "260px", fontFamily: "Outfit, sans-serif" }}>
                      <p style={{ fontSize: "10px", letterSpacing: "0.25em", textTransform: "uppercase", color: "#C16542", margin: "0 0 6px 0" }}>
                        {pick(CATEGORY_LABEL[m.kind], lang)}{m.region ? ` · ${m.region}` : ""}
                      </p>
                      <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "22px", lineHeight: 1.1, margin: "0 0 8px 0", color: "#2C2621", letterSpacing: "-0.01em" }}>
                        {m.name}
                      </h3>
                      <p style={{ fontSize: "13px", color: "#5C5248", lineHeight: 1.5, margin: "0 0 12px 0" }}>
                        {m.intro}
                      </p>

                      {/* Mini-CTA — one-click add to the /booking planner.
                          Only rendered for the three "stayable" kinds
                          (hotel, bivouac, refuge) — landmarks don't have
                          a slug and can't be part of an enquiry. */}
                      {m.slug && (
                        (() => {
                          const inEnquiry = Boolean(selectedHotels[m.slug]);
                          return (
                            <button
                              type="button"
                              onClick={() => {
                                toggleHotelWithFeedback(m.slug);
                                // After adding (i.e. it wasn't in enquiry),
                                // whisk the user straight to the planner
                                // so the map becomes the first pathway to
                                // a multi-property booking.
                                if (!inEnquiry) {
                                  window.setTimeout(() => navigate("/booking#step-01"), 250);
                                }
                              }}
                              aria-pressed={inEnquiry}
                              style={{
                                width: "100%",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "6px",
                                padding: "10px 12px",
                                fontSize: "10.5px",
                                letterSpacing: "0.25em",
                                textTransform: "uppercase",
                                background: inEnquiry ? "#2C2621" : "#C16542",
                                color: "#FDFBF7",
                                border: "none",
                                cursor: "pointer",
                                marginBottom: m.href ? "12px" : 0,
                              }}
                              data-testid={`map-popup-add-${m.key}`}
                            >
                              {inEnquiry ? (
                                <>
                                  <Check size={12} strokeWidth={2} />
                                  {pick({
                                    en: "In your enquiry",
                                    fr: "Dans votre demande",
                                    es: "En tu solicitud",
                                  }, lang)}
                                </>
                              ) : (
                                <>
                                  <Plus size={12} strokeWidth={2} />
                                  {pick({
                                    en: "Add to my enquiry",
                                    fr: "Ajouter à ma demande",
                                    es: "Añadir a mi solicitud",
                                  }, lang)}
                                </>
                              )}
                            </button>
                          );
                        })()
                      )}

                      {m.href && (
                        <Link
                          to={m.href}
                          style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "10px", letterSpacing: "0.25em", textTransform: "uppercase", color: "#2C2621", borderBottom: "1px solid #2C2621", paddingBottom: "2px", textDecoration: "none" }}
                          data-testid={`map-popup-link-${m.key}`}
                        >
                          {pick({ en: "Discover", fr: "Découvrir", es: "Descubrir" }, lang)}
                          <ArrowRight size={12} strokeWidth={1.6} />
                        </Link>
                      )}
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>

          {/* Side Panel */}
          <aside
            className="lg:col-span-4 flex flex-col bg-white rounded-3xl border border-[#E8DFD2] shadow-[0_8px_30px_-12px_rgba(44,38,33,0.18)] overflow-hidden"
            style={{ height: "640px" }}
            data-testid="map-side-panel"
          >
            {/* Search */}
            <div className="px-5 pt-5 pb-4 border-b border-[#E8DFD2]/70">
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#C16542]">
                {pick({ en: "Locations index", fr: "Index des lieux", es: "Índice de lugares" }, lang)}
              </p>
              <h3 className="font-serif-x text-xl mt-1.5 text-[#2C2621] leading-tight">
                {pick({ en: "Browse every place", fr: "Parcourir tous les lieux", es: "Recorrer todos los lugares" }, lang)}
              </h3>
              <div className="mt-4 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#A89A87]" strokeWidth={1.8} />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={pick({ en: "Search a hotel, dune, valley…", fr: "Chercher un hôtel, une dune, une vallée…", es: "Buscar un hotel, una duna, un valle…" }, lang)}
                  className="w-full bg-[#FBF6EE] border border-[#E8DFD2] focus:border-[#C16542] outline-none rounded-xl pl-9 pr-9 h-10 text-sm"
                  data-testid="map-search"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#5C5248] hover:text-[#C16542]"
                    data-testid="map-search-clear"
                    aria-label="Clear search"
                  >
                    <X className="w-3.5 h-3.5" strokeWidth={1.8} />
                  </button>
                )}
              </div>
            </div>

            {/* Scrollable list */}
            <div className="flex-1 overflow-y-auto px-3 py-3 space-y-5">
              {FILTERS.map((f) => {
                const list = grouped[f.id];
                if (!list || list.length === 0) return null;
                const Icon = f.icon;
                return (
                  <div key={f.id}>
                    <p className="px-3 mb-2 flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#5C5248]">
                      <Icon className="w-3.5 h-3.5" strokeWidth={1.6} style={{ color: COLORS[f.id] }} />
                      {pick(f.label, lang)} · {list.length}
                    </p>
                    <ul className="space-y-1">
                      {list.map((p) => {
                        const isActive = focusedId === p.key;
                        return (
                          <li
                            key={p.key}
                            ref={(el) => { if (el) panelItemRefs.current[p.key] = el; }}
                          >
                            <button
                              type="button"
                              onClick={() => focusPlace(p)}
                              className={`w-full text-left rounded-xl px-3 py-3 flex gap-3 items-start transition-colors ${
                                isActive
                                  ? "bg-[#2C2621] text-[#FDFBF7]"
                                  : "hover:bg-[#FBF6EE]"
                              }`}
                              data-testid={`map-panel-item-${p.key}`}
                            >
                              <span
                                className="mt-1.5 w-2.5 h-2.5 rounded-full shrink-0"
                                style={{
                                  background: COLORS[p.kind],
                                  boxShadow: isActive ? "0 0 0 3px rgba(212,163,115,0.45)" : "none",
                                }}
                              />
                              <span className="min-w-0 flex-1">
                                <span className={`block font-serif-x text-base leading-tight truncate ${isActive ? "text-[#FDFBF7]" : "text-[#2C2621]"}`}>
                                  {p.name}
                                </span>
                                {(p.region || p.location) && (
                                  <span className={`block text-[11px] mt-0.5 truncate ${isActive ? "text-[#FDFBF7]/70" : "text-[#5C5248]"}`}>
                                    {p.region || p.location}
                                  </span>
                                )}
                              </span>
                              {p.href && (
                                <Link
                                  to={p.href}
                                  onClick={(e) => e.stopPropagation()}
                                  className={`shrink-0 mt-0.5 text-[10px] tracking-[0.2em] uppercase border-b pb-0.5 ${
                                    isActive
                                      ? "text-[#D4A373] border-[#D4A373]/60"
                                      : "text-[#C16542] border-[#C16542]/40 hover:border-[#C16542]"
                                  }`}
                                  data-testid={`map-panel-view-${p.key}`}
                                >
                                  {pick({ en: "View", fr: "Voir", es: "Ver" }, lang)}
                                </Link>
                              )}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}

              {visible.length === 0 && (
                <p className="px-3 py-8 text-center text-sm text-[#5C5248]">
                  {pick({
                    en: "No place matches your search.",
                    fr: "Aucun lieu ne correspond à votre recherche.",
                    es: "Ningún lugar coincide con su búsqueda.",
                  }, lang)}
                </p>
              )}
            </div>

            {/* Reset */}
            <div className="px-5 py-3 border-t border-[#E8DFD2]/70 bg-[#FBF6EE]/60">
              <button
                type="button"
                onClick={() => {
                  setFocusedId(null);
                  if (mapRef.current) mapRef.current.flyToBounds(MAP_BOUNDS, { duration: 0.8 });
                }}
                className="w-full inline-flex items-center justify-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#2C2621] hover:text-[#C16542]"
                data-testid="map-reset-view"
              >
                {pick({ en: "Reset view", fr: "Vue d'ensemble", es: "Vista general" }, lang)}
                <ArrowRight className="w-3 h-3" strokeWidth={1.6} />
              </button>
            </div>
          </aside>
        </div>

        <p className="mt-4 text-[11px] tracking-[0.2em] uppercase text-[#5C5248]/70 text-center">
          {pick({
            en: "Tip — when markers overlap (Merzouga · Erg Chebbi), use the side panel to pick the exact place.",
            fr: "Astuce — quand les marqueurs se chevauchent (Merzouga · Erg Chebbi), utilisez le panneau latéral pour choisir le lieu exact.",
            es: "Sugerencia — cuando los marcadores se superpongan (Merzouga · Erg Chebbi), use el panel lateral para elegir el lugar exacto.",
          }, lang)}
        </p>
      </div>
    </section>
  );
};

export default MapSection;
